/**
 * BotAssignment Component
 *
 * Displays and manages bot assignment for WhatsApp numbers and social channel integrations.
 * Supports dual-mode operation:
 * - WhatsApp mode: updates bot.numeroId field
 * - Integration mode: updates integration.botId field (Instagram, TikTok, Messenger)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from './Toast';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { API_URL } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';

const BotAssignment = ({
  uid,
  numeroId,         // For WhatsApp mode
  integrationType,  // 'whatsapp' | 'instagram' | 'tiktok' | 'messenger'
  integrationId,    // For integration mode (userId or pageId)
  accountId,        // For channelAccount mode (per-account list) → assignBotToChannelAccount callable
  currentBotId
}) => {
  const { t } = useTranslation('channels');
  const { context } = useOrganization();
  // Default to WhatsApp mode if integrationType not specified
  const mode = integrationType || 'whatsapp';
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(currentBotId || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Helper to get bots path (org/personal)
  const getBotsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'bots']
    : ['clientes', auth.currentUser?.uid || uid, 'bots'];

  // Helper to get integrations path (org/personal)
  const getIntegrationsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'integrations']
    : ['clientes', auth.currentUser?.uid || uid, 'integrations'];

  // Load all bots for this client
  useEffect(() => {
    const loadBots = async () => {
      if (!uid) return;

      setLoading(true);
      try {
        const botsSnapshot = await getDocs(collection(db, ...getBotsPath()));
        const botsList = botsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBots(botsList);
      } catch (error) {
        console.error('Error loading bots:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBots();
  }, [uid, context.mode, context.orgId]);

  // Update selected bot when currentBotId changes
  useEffect(() => {
    setSelectedBot(currentBotId || '');
  }, [currentBotId]);

  const handleAssignBot = async () => {
    if (!selectedBot) {
      toast.info(t('botAssignment.selectBotToast'));
      return;
    }

    setSaving(true);
    try {
      if (mode === 'whatsapp') {
        // WhatsApp mode: Call Cloud Function to assign bot (creates numero if missing)
        try {
          // Get Firebase auth token
          const idToken = await auth.currentUser.getIdToken();

          // Call Cloud Function to assign bot
          const response = await fetch(`${API_URL}/assignBotToNumero`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              numeroId: numeroId,
              botId: selectedBot,
              previousBotId: currentBotId || null,
              organizationId: context.mode === 'organization' ? context.orgId : null
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to assign bot');
          }

          const result = await response.json();
          console.log('Bot assigned successfully:', result);
        } catch (fetchError) {
          console.error('Error calling assignBotToNumero:', fetchError);
          throw fetchError;
        }
      } else if (accountId) {
        // channelAccount mode (per-account list) → assignBotToChannelAccount callable.
        // Writes channelAccount.botId + channel_lookup.botId atomically (server-only).
        const idToken = await auth.currentUser.getIdToken();
        const response = await fetch(`${API_URL}/assignBotToChannelAccount`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            accountId,
            botId: selectedBot,
            organizationId: context.mode === 'organization' ? context.orgId : null
          })
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || t('botAssignment.assignError'));
        }
      } else {
        // Integration mode: Update integration.botId field
        const integrationRef = doc(db, ...getIntegrationsPath(), mode);
        await updateDoc(integrationRef, {
          botId: selectedBot
        });
      }

      toast.success(t('botAssignment.assignSuccess'));
      if (!accountId) {
        window.location.reload(); // legacy modes refresh; channelAccount updates live via onSnapshot
      }
    } catch (error) {
      console.error('Error assigning bot:', error);
      // channelAccount surfaces the server's legible 404/422 message (or a network-error message);
      // legacy modes keep the generic toast.
      toast.error(accountId ? (error.message || t('botAssignment.assignError')) : t('botAssignment.assignError'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignBot = async () => {
    if (!currentBotId) return;

    const channelName = mode === 'whatsapp' ? t('botAssignment.channelNameNumber') : t('botAssignment.channelNameChannel');
    const confirmed = window.confirm(t('botAssignment.confirmUnassign', { channelName }));
    if (!confirmed) return;

    setSaving(true);
    try {
      if (mode === 'whatsapp') {
        // WhatsApp mode: Call Cloud Function to unassign (clears both bot and numero)
        try {
          // Get Firebase auth token
          const idToken = await auth.currentUser.getIdToken();

          // Call Cloud Function to unassign bot
          const response = await fetch(`${API_URL}/unassignBotFromNumero`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({
              numeroId: numeroId,
              botId: currentBotId,
              organizationId: context.mode === 'organization' ? context.orgId : null
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to unassign bot');
          }

          const result = await response.json();
          console.log('Bot unassigned successfully:', result);
        } catch (fetchError) {
          console.error('Error calling unassignBotFromNumero:', fetchError);
          throw fetchError;
        }
      } else {
        // Integration mode: Clear integration.botId
        const integrationRef = doc(db, ...getIntegrationsPath(), mode);
        await updateDoc(integrationRef, {
          botId: null
        });
      }

      toast.success(t('botAssignment.unassignSuccess'));
      window.location.reload();
    } catch (error) {
      console.error('Error unassigning bot:', error);
      toast.error(t('botAssignment.unassignError'));
    } finally {
      setSaving(false);
    }
  };

  // Get current bot info
  const currentBot = bots.find(b => b.id === currentBotId);

  // All bots available — reassignment is handled by assignBotToNumero
  const availableBots = bots;

  if (loading) {
    return <div style={{ fontSize: '12px', color: '#666' }}>{t('botAssignment.loading')}</div>;
  }

  return (
    <div style={{
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #e0e0e0'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
          {t('botAssignment.assignedLabel')}
        </span>
        {currentBot && !accountId && (
          <button
            onClick={handleUnassignBot}
            disabled={saving}
            style={{
              fontSize: '11px',
              padding: '4px 8px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {t('botAssignment.unassignBtn')}
          </button>
        )}
      </div>

      {currentBot ? (
        <div style={{
          padding: '8px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          marginBottom: '8px'
        }}>
          <div style={{ fontSize: '13px', color: '#155724', fontWeight: '500' }}>
            ✅ {currentBot.config?.name || t('botAssignment.unnamed')}
          </div>
          <div style={{ fontSize: '11px', color: '#155724', marginTop: '4px' }}>
            {currentBot.config?.description || t('botAssignment.noDescription')}
          </div>
        </div>
      ) : (
        <div style={{
          padding: '8px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          marginBottom: '8px',
          fontSize: '12px',
          color: '#856404'
        }}>
          {t('botAssignment.noBot')}
        </div>
      )}

      {/* Bot Selector */}
      <div style={{ marginTop: '12px' }}>
        <label style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: '500',
          color: '#555',
          marginBottom: '6px'
        }}>
          {currentBot ? t('botAssignment.changeBot') : t('botAssignment.assignBot')}
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={selectedBot}
            onChange={(e) => setSelectedBot(e.target.value)}
            disabled={saving}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: 'white'
            }}
          >
            <option value="">{t('botAssignment.selectPlaceholder')}</option>
            {availableBots.map(bot => (
              <option key={bot.id} value={bot.id}>
                {bot.config?.name || t('botAssignment.unnamed')} {bot.id === currentBotId ? t('botAssignment.currentIndicator') : ''}
              </option>
            ))}
          </select>
          <button
            onClick={handleAssignBot}
            disabled={saving || !selectedBot || selectedBot === currentBotId}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (saving || !selectedBot || selectedBot === currentBotId) ? 'not-allowed' : 'pointer',
              opacity: (saving || !selectedBot || selectedBot === currentBotId) ? 0.6 : 1,
              whiteSpace: 'nowrap'
            }}
          >
            {saving ? t('botAssignment.savingBtn') : t('botAssignment.assignBtn')}
          </button>
        </div>
      </div>

      {bots.length === 0 && !loading && (
        <div style={{
          marginTop: '12px',
          padding: '8px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#721c24'
        }}>
          {t('botAssignment.emptyState')}
        </div>
      )}
    </div>
  );
};

export default BotAssignment;
